// This file defines useDeepCompareEffectForMaps 
// The official documentation describes using this hack: https://developers.google.com/maps/documentation/javascript/react-map#add-map
// It is basically a copy of the code the official docs refer to: https://github.com/googlemaps/js-samples/blob/fab4f8441d5eb6517f3939bf9fab25fec59bdad4/samples/react-map/index.tsx#L133-L140

/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-argument */

import { isLatLngBoundsOrLatLngBoundsLiteral, isLatLngLiteral } from "@googlemaps/typescript-guards";
import { createCustomEqual, deepEqual, EqualityComparator } from "fast-equals";
import { useEffect, useRef } from "react";

const deepCompareEqualsForMaps = createCustomEqual(
  (deepEqual) => (a: any, b: any) => {
    if (
      isLatLngLiteral(a) || a instanceof google.maps.LatLng ||
      isLatLngLiteral(b) || b instanceof google.maps.LatLng
    ) {
      return new google.maps.LatLng(a).equals(new google.maps.LatLng(b));
    }

    if (
      isLatLngBoundsOrLatLngBoundsLiteral(a) || a instanceof google.maps.LatLngBounds ||
      isLatLngBoundsOrLatLngBoundsLiteral(b) || b instanceof google.maps.LatLngBounds
    ) {
      return new google.maps.LatLngBounds(a).equals(new google.maps.LatLngBounds(b));
    }
    // TODO extend to other types

    // use fast-equals for other objects
    return deepEqual(a, b);
  }
);
  
function useDeepCompareMemoize(value: any, comparator: EqualityComparator = deepEqual) {
  const ref = useRef();

  if (!comparator(value, ref.current)) {
    ref.current = value;
  }

  return ref.current;
}
  

function useDeepCompareEffect(
  callback: React.EffectCallback,
  dependencies: any[],
  comparator: EqualityComparator = deepEqual,
) {
  useEffect(callback, dependencies.map(dependency => useDeepCompareMemoize(dependency, comparator)));
}

export function useDeepCompareEffectForMaps(
  callback: React.EffectCallback,
  dependencies: any[],
) {
  useDeepCompareEffect(callback, dependencies, deepCompareEqualsForMaps)
}


function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}


/**
 * 
 * A custom effect hook to run custom side effects whenever a map (with key-value pairs) is changed.
 * Deep equality comparison is used for the values.
 * 
 * @param map The map the check for changes, and run callbacks if keys/values changed
 * @param onKeyAdded The handler to run if a key-value pair was added to the map
 * @param onKeyUpdated The handler to run if the value for a specific key-value pair was changed. Deep equality is used to check modification.
 * If `onKeyUpdated` was no provided, the `onKeyDeleted` and `onKeyAdded` handlers will be invoked.
 * 
 * @param onKeyDeleted The handler to run if a key-value pair was removed from the map
 */
export const useEffectOnMapChange = <K, V,>(
  map: Map<K, V>,
  onKeyAdded?: (k: K, v:V) => void, 
  onKeyUpdated?: (k: K, v_prev:V, v_new:V) => void, 
  onKeyDeleted?: (k: K, v:V) => void) => {
  
  const mapClone = new Map(map)
  const prevMap = usePrevious(mapClone) ?? new Map<K, V>()

  // If no "key updated" handler specified, we invoke "onKeyDeleted" and "onKeyAdded"
  if (!onKeyUpdated && (onKeyAdded || onKeyDeleted)) {
    onKeyUpdated = (k, vPrev, vNew) => {
      onKeyDeleted?.(k, vPrev)
      onKeyAdded?.(k, vNew)
    }
  }

  useDeepCompareEffect(() => {
    map.forEach((v, k) => {
      if (onKeyAdded && !prevMap.has(k)) {
          onKeyAdded(k, v)
      }
      else if (onKeyUpdated && !deepEqual(v, prevMap.get(k))) {
        onKeyUpdated(k, (prevMap.get(k) as V), v)
      }
    })
    if (onKeyDeleted) {
      prevMap.forEach((v, k) => {
        if (!map.has(k)) {
          onKeyDeleted(k, v)
        }
      })
    }
  }, [mapClone])
}